import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MailIcon, MapPinIcon, GraduationCapIcon, LinkedinIcon } from 'lucide-react';
export function ContactSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full py-24 px-6 bg-slate-950" id="contact" data-id="element-5">
      <div className="max-w-5xl mx-auto" data-id="element-6">
        {/* Heading */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-7">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-slate-100" data-id="element-8">
            Get In Touch
          </h2>
          <p className="text-lg text-slate-400 font-mono" data-id="element-9">
            {'>'} Let's discuss your data challenges
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-10">
          {/* ================= EDUCATION (FIRST) ================= */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: 30
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 backdrop-blur-sm" data-id="element-11">
            <h3 className="text-2xl font-bold mb-6 text-slate-100" data-id="element-12">
              Education
            </h3>

            <div className="space-y-6" data-id="element-13">
              {/* MSc */}
              <div className="flex items-start gap-4" data-id="element-14">
                <div className="p-3 bg-teal-500/10 rounded-lg flex-shrink-0" data-id="element-15">
                  <GraduationCapIcon className="h-5 w-5 text-teal-400" data-id="element-16" />
                </div>
                <div data-id="element-17">
                  <h4 className="font-bold text-slate-200 mb-1" data-id="element-18">
                    M.Sc. Artificial Intelligence & Data Science
                  </h4>
                  <p className="text-sm text-slate-400 mb-2" data-id="element-19">
                    Helwan University
                  </p>
                  <p className="text-xs text-slate-500 font-mono" data-id="element-20">
                    Oct 2023 - Present
                  </p>
                  <p className="text-sm text-teal-400 mt-2 font-medium" data-id="element-21">
                    Research: Enhancing fault localization in Python.
                  </p>
                </div>
              </div>

              {/* Diploma */}
              <div className="flex items-start gap-4" data-id="element-22">
                <div className="p-3 bg-orange-500/10 rounded-lg flex-shrink-0" data-id="element-23">
                  <GraduationCapIcon className="h-5 w-5 text-orange-400" data-id="element-24" />
                </div>
                <div data-id="element-25">
                  <h4 className="font-bold text-slate-200 mb-1" data-id="element-26">
                    Postgraduate Diploma, Computer Science Engineering
                  </h4>
                  <p className="text-sm text-slate-400 mb-2" data-id="element-27">
                    Kafr El-Sheikh Univ.
                  </p>
                  <p className="text-xs text-slate-500 font-mono" data-id="element-28">
                    Sep 2022 - Jun 2023
                  </p>
                  <p className="text-sm text-teal-400 mt-2 font-medium" data-id="element-29">
                    Grade: Excellent.
                    
                  </p>
                </div>
              </div>

              {/* BSc */}
              <div className="flex items-start gap-4" data-id="element-30">
                <div className="p-3 bg-purple-500/10 rounded-lg flex-shrink-0" data-id="element-31">
                  <GraduationCapIcon className="h-5 w-5 text-purple-400" data-id="element-32" />
                </div>
                <div data-id="element-33">
                  <h4 className="font-bold text-slate-200 mb-1" data-id="element-34">
                    B.Sc. Computer Science
                  </h4>
                  <p className="text-sm text-slate-400 mb-2" data-id="element-35">
                    Higher Institute of Engineering & Technology
                  </p>
                  <p className="text-xs text-slate-500 font-mono" data-id="element-36">
                    2017 - 2021
                  </p>
                  <p className="text-sm text-teal-400 mt-2 font-medium" data-id="element-37">
                    Grade: Excellent with Honors (92.37%).
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ================= CONTACT INFO (SECOND) ================= */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 backdrop-blur-sm" data-id="element-38">
            <h3 className="text-2xl font-bold mb-6 text-slate-100" data-id="element-39">
              Contact Information
            </h3>

            <div className="space-y-4" data-id="element-40">
              {/* Email */}
              <a href="mailto:danaelshrbiny55@gmail.com" className="flex items-center gap-4 text-slate-300 hover:text-teal-400 transition-colors group" data-id="element-41">
                <div className="p-3 bg-teal-500/10 rounded-lg group-hover:bg-teal-500/20 transition-colors" data-id="element-42">
                  <MailIcon className="h-5 w-5 text-teal-400" data-id="element-43" />
                </div>
                <div data-id="element-44">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-45">
                    Email
                  </div>
                  <div className="font-medium" data-id="element-46">danaelshrbiny55@gmail.com</div>
                </div>
              </a>

              {/* LinkedIn */}
              <a href="https://www.linkedin.com/in/danaelshrbiny" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-slate-300 hover:text-blue-400 transition-colors group" data-id="element-47">
                <div className="p-3 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors" data-id="element-48">
                  <LinkedinIcon className="h-5 w-5 text-blue-400" data-id="element-49" />
                </div>
                <div data-id="element-50">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-51">
                    LinkedIn
                  </div>
                  <div className="font-medium" data-id="element-52">Dana Elshrbiny</div>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-center gap-4 text-slate-300" data-id="element-53">
                <div className="p-3 bg-purple-500/10 rounded-lg" data-id="element-54">
                  <MapPinIcon className="h-5 w-5 text-purple-400" data-id="element-55" />
                </div>
                <div data-id="element-56">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-57">
                    Location
                  </div>
                  <div className="font-medium" data-id="element-58">Egypt</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="mt-16 text-center" data-id="element-59">
          <p className="text-slate-500 text-sm font-mono" data-id="element-60">
            © 2026 Dana Elshrbiny
          </p>
        </motion.div>
      </div>
    </section>;
}